import { App, Plugin } from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import type { HomePortalAppState } from "../app";
import { RegistrationPage } from "../components/registration-page";
import { APP_ROUTES } from "../constants";
import { submitRegistration } from "../helpers/fetch-registration";

export interface HomePluginState {
  registration: {
    isSubmitting?: boolean;
    submitStatus?: {
      type: "success" | "error" | null;
      message: string;
      code?: string;
    };
    showSuccessDialog?: boolean;
  };
}

export class HomePlugin implements Plugin<HomePortalAppState> {
  private app?: App<HomePortalAppState>;
  route?: Plugin<HomePortalAppState>["route"];

  onRegister(app: App<HomePortalAppState>): void {
    this.app = app;

    const view = app.createView({
      name: "Home",
    });

    const registrationComponent = view.addComponent({
      component: RegistrationPage,
    });

    registrationComponent.mapState((state) => ({
      isSubmitting: state.registration?.isSubmitting ?? false,
      submitStatus: state.registration?.submitStatus ?? {
        type: null,
        message: "",
      },
      showSuccessDialog: state.registration?.showSuccessDialog ?? false,
      language: state.language,
    }));

    registrationComponent.on("register", async ({ payload }) => {
      if (!payload?.register) return;

      const formData = payload.register;

      this.app?.patchAppState({
        registration: {
          isSubmitting: true,
          submitStatus: { type: null, message: "" },
        },
      });

      try {
        const result = await submitRegistration({
          ...formData,
          language: app.getAppState().language,
        });

        if (result.success) {
          this.app?.patchAppState({
            registration: {
              showSuccessDialog: true,
              isSubmitting: false,
              submitStatus: { type: null, message: "" },
            },
          });
        } else {
          const lang = this.app?.getAppState()?.language || "de";
          this.app?.patchAppState({
            registration: {
              isSubmitting: false,
              submitStatus: {
                type: "error",
                message:
                  result.message ||
                  getLocalizedMessage(lang, "homePage.registration.failed"),
                ...(result.code ? { code: result.code } : {}),
              },
            },
          });
        }
      } catch {
        const lang = this.app?.getAppState()?.language || "de";
        this.app?.patchAppState({
          registration: {
            isSubmitting: false,
            submitStatus: {
              type: "error",
              message: getLocalizedMessage(
                lang,
                "homePage.registration.unexpectedError"
              ),
            },
          },
        });
      }
    });

    registrationComponent.on("change-language", ({ payload }) => {
      if (!payload?.language) return;
      app.patchAppState({ language: payload.language });
    });

    registrationComponent.on("close-success-dialog", () => {
      this.app?.patchAppState({
        registration: {
          showSuccessDialog: false,
          submitStatus: { type: null, message: "" },
        },
      });
    });

    registrationComponent.on("clear-error", () => {
      this.app?.patchAppState({
        registration: {
          submitStatus: { type: null, message: "" },
        },
      });
    });

    this.route = app.createRoute({
      path: APP_ROUTES.home,
      view,
      isOverlayChild: false,
    });

    this.route?.on("load", () => {
      const requestedLanguage =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("language")
          : null;
      if (requestedLanguage === "de" || requestedLanguage === "en") {
        app.patchAppState({ language: requestedLanguage });
        return;
      }

      const persistedLanguage =
        typeof window !== "undefined"
          ? window.localStorage.getItem("language")
          : null;
      if (persistedLanguage !== "de" && persistedLanguage !== "en") {
        return;
      }

      if (app.getAppState().language === persistedLanguage) {
        return;
      }

      app.patchAppState({ language: persistedLanguage });
    });
  }
}
